import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../../shared/Header/Header';
import Footer from '../../../shared/Footer/Footer';
import Section from '../../../layouts/showcaseLayouts/Section/Section';
import SectionBody from '../../../layouts/showcaseLayouts/Section/SectionBody/SectionBody';
// import SectionHeader from '../../../layouts/showcaseLayouts/Section/SectionHeader/SectionHeader';
// import BackButton from '../../../shared/backButton/BackButton';
import Placeholder from '../../../UI/Placeholder/Placeholder';
import OverQuantityModal from '../../../UI/OverQuantityModal/OverQuantityModal';
import useForm from '../../../../hooks/useForm';
import useIsLTE650 from '../../../../hooks/useIsLTE650';
import { NO_PRODUCTS_IN_CART } from '../../../../constants/messages';
import type { AppDispatch, RootState } from '../../../../store/store';
import { removeProductFromCart, setToLocalStorage, wishListHandler } from '../../../../store/UserSlice';
import type { AddressStruct, CartItem, ProductCartItem } from '../../../../types/common';
import { cartFormValidator } from '../../../../utils/validators';
import { ACTIVATING_DISCOUNT_MANAGEMENT, FREE_DELIVERY_MIN_TOTAL } from '../../../../lib/cartPage/config';
import { useCartPricing } from '../../../../hooks/cartPage/useCartPricing';
import { useCartDiscounts } from '../../../../hooks/cartPage/useCartDiscounts';
import { useShipping } from '../../../../hooks/cartPage/useShipping';
import { useCheckoutSubmit } from '../../../../hooks/cartPage/useCheckoutSubmit';
import ActionLinkButton from '../../../UI/ActionLinkButton/ActionLinkButton';
import { PATHS } from '../../../../constants/routes';
import { CartItemsSection } from './modules/CartItemsSection/CartItemsSection';
// import { PromoUpsellSection } from './modules/PromoUpsellSection/PromoUpsellSection';
import { RecipientSection, type RecipientSectionHandle } from './modules/RecipientSection/RecipientSection';
import { DeliverySection } from './modules/DeliverySection/DeliverySection';
import { PaymentSection } from './modules/PaymentSection/PaymentSection';
import { CommentsSection } from './modules/CommentsSection/CommentsSection';
import { StickySummary } from './modules/StickySummary/StickySummary';
import './cartPageNew.scss';

type CommonErrorLike = { isError?: boolean; message?: string } | null;
type SelectedDeliveryAddress = {
    raw: string;
    struct: AddressStruct | null;
    comment?: string;
} | null;
type RepeatOrderSelectionState = {
    repeatOrderSelection?: {
        productIds?: unknown;
        token?: unknown;
    };
} | null;

function readRepeatOrderSelection(state: unknown): { productIds: string[]; token: string } | null {
    if (!state || typeof state !== 'object') return null;
    const raw = (state as RepeatOrderSelectionState)?.repeatOrderSelection;
    if (!raw || typeof raw !== 'object') return null;
    const productIdsRaw = Array.isArray(raw.productIds) ? raw.productIds : [];
    const productIds = Array.from(new Set(productIdsRaw.map((item) => String(item ?? '').trim()).filter(Boolean)));
    const token = typeof raw.token === 'string' ? raw.token.trim() : '';
    if (!token || !productIds.length) return null;
    return { productIds, token };
}
function splitHumanName(value: unknown): { lastName: string; firstName: string } {
    const parts = String(value ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    return {
        lastName: parts[0] ?? '',
        firstName: parts[1] ?? '',
    };
}

function readString(obj: unknown, key: string): string {
    if (!obj || typeof obj !== 'object') return '';
    const v = (obj as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : '';
}
// function readBoolean(obj: unknown, key: string, defaultValue = false): boolean {
//     if (!obj || typeof obj !== 'object') return defaultValue;
//     const v = (obj as Record<string, unknown>)[key];
//     return typeof v === 'boolean' ? v : defaultValue;
// }
function readAddressRaw(user: unknown): string {
    if (!user || typeof user !== 'object') return '';
    const addr = (user as Record<string, unknown>).address;
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    if (typeof addr === 'object') {
        const raw = (addr as Record<string, unknown>).raw;
        return typeof raw === 'string' ? raw : '';
    }
    return '';
}
function readAddressContainer(user: unknown): SelectedDeliveryAddress {
    if (!user || typeof user !== 'object') return null;
    const addr = (user as Record<string, unknown>).address;
    if (!addr) return null;
    if (typeof addr === 'string') {
        const raw = addr.trim();
        return raw ? { raw, struct: null } : null;
    }
    if (typeof addr === 'object') {
        const raw = typeof (addr as Record<string, unknown>).raw === 'string' ? String((addr as Record<string, unknown>).raw).trim() : '';
        const structUnknown = (addr as Record<string, unknown>).struct;
        const comment = typeof (addr as Record<string, unknown>).comment === 'string' ? String((addr as Record<string, unknown>).comment).trim() : '';
        const struct = structUnknown && typeof structUnknown === 'object' ? (structUnknown as AddressStruct) : null;
        if (!raw) return null;
        return { raw, struct, ...(comment ? { comment } : {}) };
    }
    return null;
}
function hasSavedDeliveryAddresses(user: unknown): boolean {
    if (readAddressContainer(user)?.raw) return true;
    if (!user || typeof user !== 'object') return false;
    const addresses = (user as Record<string, unknown>).addresses;
    if (!Array.isArray(addresses)) return false;
    return addresses.some((item) => {
        if (!item || typeof item !== 'object') return false;
        const raw = typeof (item as Record<string, unknown>).raw === 'string' ? String((item as Record<string, unknown>).raw).trim() : '';
        const isDisabled = Boolean((item as Record<string, unknown>).isDisabled);
        return !isDisabled && raw !== '';
    });
}
function toCommonErrorLike(v: unknown): CommonErrorLike {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    const isError = typeof o.isError === 'boolean' ? o.isError : undefined;
    const message = typeof o.message === 'string' ? o.message : undefined;
    if (isError === undefined && message === undefined) return null;
    return { isError, message };
}
function hasRequiredRecipientData(user: unknown, fallbackPhone = ''): boolean {
    const lastName = readString(user, 'lastName').trim();
    const firstName = readString(user, 'firstName').trim();
    const phone = (readString(user, 'phoneNumber') || String(fallbackPhone ?? '')).trim();
    return lastName !== '' && firstName !== '' && phone !== '';
}

const CartPageNew: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsLTE650(650);

    const { cart, wishlist } = useSelector((s: RootState) => s.user);
    const { products } = useSelector((s: RootState) => s.product);
    const { error, isLoading } = useSelector((s: RootState) => s.common);
    const authUser = useSelector((s: RootState) => s.auth.user);
    const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);

    const hasProducts = cart.length > 0;
    const repeatOrderSelection = useMemo(() => readRepeatOrderSelection(location.state), [location.state]);
    const currentCartIds = useMemo(() => cart.map((item) => item.productId), [cart]);
    const [selectedIds, setSelectedIds] = useState<string[]>(() => currentCartIds);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const prevCartIdsRef = useRef<string[]>(currentCartIds);
    const appliedRepeatSelectionTokenRef = useRef<string>('');

    useEffect(() => {
        setSelectedIds((prevSelected) => {
            if (!currentCartIds.length) return [];
            const prevCartIds = prevCartIdsRef.current;
            if (!prevCartIds.length && !prevSelected.length) {
                return currentCartIds;
            }
            const prevCartSet = new Set(prevCartIds);
            const nextSelected = currentCartIds.filter((id) => prevSelected.includes(id) || !prevCartSet.has(id));
            const sameLength = nextSelected.length === prevSelected.length;
            const sameOrder = sameLength && nextSelected.every((id, index) => id === prevSelected[index]);
            return sameOrder ? prevSelected : nextSelected;
        });
        prevCartIdsRef.current = currentCartIds;
    }, [currentCartIds]);

    useEffect(() => {
        if (!repeatOrderSelection?.token) return;
        if (appliedRepeatSelectionTokenRef.current === repeatOrderSelection.token) return;
        if (!currentCartIds.length) return;
        const requestedIdsSet = new Set(repeatOrderSelection.productIds);
        const nextSelectedIds = currentCartIds.filter((id) => requestedIdsSet.has(id));
        setSelectedIds(nextSelectedIds);
        appliedRepeatSelectionTokenRef.current = repeatOrderSelection.token;
    }, [repeatOrderSelection, currentCartIds]);

    /* -------- pricing -------- */
    const fullPricing = useCartPricing({ cart, products, wishlist, authUser });
    const cartProducts = fullPricing.cartProducts;
    const fullTotals = fullPricing.totals;

    const selectedCart = useMemo(() => cart.filter((item) => selectedSet.has(item.productId)), [cart, selectedSet]);
    const hasSelectedProducts = selectedCart.length > 0;

    const pricing = useCartPricing({ cart: selectedCart, products, wishlist, authUser });
    const selectedCartProducts = pricing.cartProducts;
    const totals = pricing.totals;
    // const flags = pricing.flags;
    const hasSecondPillowPromo = pricing.hasSecondPillowPromo;

    const originalTotal = totals.price;
    const summaryBaseGoodsTotal = useMemo(() => {
        const total = selectedCartProducts.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0);
        return +total.toFixed(2);
    }, [selectedCartProducts]);

    /* -------- discounts -------- */
    const discounts = useCartDiscounts({
        authUser,
        cartProducts: selectedCartProducts,
        originalTotal,
        hasSecondPillowPromo,
    });

    const {
        promoCodeInput,
        setPromoCodeInput,
        appliedPromo,
        setAppliedPromo,
        promoError,
        setPromoError,
        handleApplyPromo,
        birthdayEligible: birthdayEligibleRaw,
        bonusEligible: bonusEligibleRaw,
        showDiscountsUnavailableNotice,
        selectedDiscount: selectedDiscountRaw,
        setSelectedDiscount: setSelectedDiscountRaw,
        goodsPayable,
        sumSpecialEmployee,
        isEmployee,
    } = discounts;
    const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<SelectedDeliveryAddress>(() => readAddressContainer(authUser));

    const birthdayEligible = ACTIVATING_DISCOUNT_MANAGEMENT ? birthdayEligibleRaw : false;
    const bonusEligible = ACTIVATING_DISCOUNT_MANAGEMENT ? bonusEligibleRaw : null;
    const selectedDiscount = ACTIVATING_DISCOUNT_MANAGEMENT ? selectedDiscountRaw : null;
    // const setSelectedDiscount = ACTIVATING_DISCOUNT_MANAGEMENT ? setSelectedDiscountRaw : () => undefined;

    /* -------- shipping -------- */
    const shipping = useShipping({
        authUser,
        selectedDeliveryAddress,
        cartProducts: selectedCartProducts,
        weight: totals.weight,
        goodsPayable,
        appliedPromo,
        selectedDiscount,
        bonusEligible,
    });

    const { shippingMethod, setShippingMethod, shippingError, isFreeDeliveryEligible, isFreeDeliveryZone, effectiveDeliveryMethod, effectiveShippingCost } =
        shipping;

    const hasSavedDeliveryAddressesValue = useMemo(() => hasSavedDeliveryAddresses(authUser), [authUser]);
    const shippingStatusText = useMemo(() => {
        if (shippingMethod !== 'dpd') return '';
        if (isFreeDeliveryEligible) return '';
        const normalizedShippingError = shippingError.trim();
        if (normalizedShippingError) return normalizedShippingError;
        if (isAuthenticated && !hasSavedDeliveryAddressesValue) {
            return 'добавьте адрес доставки';
        }
        return '';
    }, [shippingMethod, isFreeDeliveryEligible, shippingError, isAuthenticated, hasSavedDeliveryAddressesValue]);

    const payableTotal = useMemo(() => {
        if (!hasSelectedProducts) return 0;
        const deliveryPart = effectiveDeliveryMethod !== 'pickup' && effectiveShippingCost != null ? effectiveShippingCost : 0;
        return +(goodsPayable + deliveryPart).toFixed(2);
    }, [hasSelectedProducts, goodsPayable, effectiveDeliveryMethod, effectiveShippingCost]);

    /* -------- UI state -------- */
    const [payWithSBP, setPayWithSBP] = useState(false);
    const [payWithInstallments, setPayWithInstallments] = useState(false); // заложено без логики (для оплаты долями)

    const recipientScrollRef = useRef<HTMLDivElement | null>(null);
    const recipientSectionRef = useRef<RecipientSectionHandle | null>(null);
    const deliveryRef = useRef<HTMLElement | null>(null);
    const [checkoutAttempted, setCheckoutAttempted] = useState(false);
    const [deliveryNoteHighlight, setDeliveryNoteHighlight] = useState(false);

    /* -------- user data missing -------- */
    // const userDataMissing = useMemo(() => {
    //     const lastName = readString(authUser, 'lastName');
    //     const firstName = readString(authUser, 'firstName');
    //     const phone = readString(authUser, 'phoneNumber');
    //     const email = readString(authUser, 'email');
    //     const requiredMissing = lastName.trim() === '' || firstName.trim() === '' || phone.trim() === '';
    //     const emailTrimmed = email.trim();
    //     const emailMissingOrInvalid = emailTrimmed === '' || !EMAIL_RE.test(emailTrimmed);
    //     // emailStatus НЕ блокирует оформление заказа (подтверждение - опционально)
    //     return requiredMissing || emailMissingOrInvalid;
    // }, [authUser]);

    /* -------- form -------- */
    const INIT_INPUT = useMemo(
        () => ({
            name: `${readString(authUser, 'lastName')} ${readString(authUser, 'firstName')} ${readString(authUser, 'middleName')}`.trim(),
            phone: readString(authUser, 'phoneNumber'),
            address: readAddressRaw(authUser),
            orderComment: '',
        }),
        [authUser],
    );

    const submitOrderRef = useRef<null | (() => Promise<void>)>(null);
    const handleSubmitFromForm = useCallback(async () => {
        if (submitOrderRef.current) await submitOrderRef.current();
    }, []);

    const { input, handleChange, errors, resetForm } = useForm(INIT_INPUT, handleSubmitFromForm, cartFormValidator);

    useEffect(() => {
        const addressFilled = (input.address ?? '').trim() !== '';
        const requiresAddress = effectiveDeliveryMethod !== 'pickup';
        if (!requiresAddress || addressFilled) {
            setDeliveryNoteHighlight(false);
        }
    }, [input.address, effectiveDeliveryMethod]);

    /* -------- user data missing -------- */
    const userDataMissing = useMemo(() => {
        const authLastName = readString(authUser, 'lastName').trim();
        const authFirstName = readString(authUser, 'firstName').trim();
        const fallbackName = splitHumanName(input.name);
        const lastName = authLastName || fallbackName.lastName;
        const firstName = authFirstName || fallbackName.firstName;
        const phone = (readString(authUser, 'phoneNumber') || String(input.phone ?? '')).trim();
        return lastName === '' || firstName === '' || phone === '';
    }, [authUser, input.name, input.phone]);

    /* -------- submit hook -------- */
    const checkout = useCheckoutSubmit({
        dispatch,
        navigate,
        cartProducts: selectedCartProducts as unknown as ProductCartItem[],
        payableTotal,
        weight: totals.weight,
        profit: totals.profit,
        quantity: totals.quantity,
        authUser,
        input,
        selectedDeliveryAddress,
        selectedDiscount,
        bonusEligible,
        originalTotal,
        payWithSBP,
        payWithInstallments,
        appliedPromo,
        effectiveDeliveryMethod,
        effectiveShippingCost,
        commonError: toCommonErrorLike(error) as CommonErrorLike,
        isLoading,
        resetForm,
    });

    submitOrderRef.current = checkout.submitOrder;

    /* -------- selection -------- */
    // const [selectedIds, setSelectedIds] = useState<string[]>([]);
    // const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }, []);

    const selectAll = useCallback(() => {
        setSelectedIds(currentCartIds);
    }, [currentCartIds]);

    const clearSelection = useCallback(() => setSelectedIds([]), []);

    const removeSelected = useCallback(() => {
        const ids = Array.from(selectedSet);
        if (!ids.length) return;
        for (const id of ids) dispatch(removeProductFromCart(id));
        dispatch(setToLocalStorage('cart'));
        setSelectedIds([]);
    }, [dispatch, selectedSet]);

    /* -------- wishlist/remove handlers -------- */
    const handleWishlist = useCallback(
        ({ id, isWished }: { id: CartItem['productId']; isWished: boolean }) => {
            dispatch(wishListHandler({ id, isWished }));
        },
        [dispatch],
    );

    const handleRemoveCartItem = useCallback(
        (id: CartItem['productId']) => {
            dispatch(removeProductFromCart(id));
            dispatch(setToLocalStorage('cart'));
        },
        [dispatch],
    );

    /* -------- promo cancel / discount select -------- */
    const clearPromo = useCallback(() => {
        setAppliedPromo(null);
        setPromoCodeInput('');
        setPromoError('');
    }, [setAppliedPromo, setPromoCodeInput, setPromoError]);

    const handleSelectDiscount = useCallback(
        (v: 'birthday' | 'bonus' | null) => {
            if (!ACTIVATING_DISCOUNT_MANAGEMENT) return;
            // если включаем ДР/бонусы - промо обязано быть снято, т.к. хук этого сам не делает
            if (v !== null && appliedPromo) clearPromo();
            setSelectedDiscountRaw(v);
        },
        [appliedPromo, clearPromo, setSelectedDiscountRaw],
    );

    const handleCancelPromo = useCallback(() => {
        clearPromo();
    }, [clearPromo]);

    /* -------- show promo input -------- */
    // const showPromoInput = useMemo(() => {
    //     const cartHasDiscount = Boolean(flags?.cartHasDiscount);
    //     const cartHasSpecial = Boolean(flags?.cartHasSpecial);
    //     const discountSelected = ACTIVATING_DISCOUNT_MANAGEMENT ? selectedDiscount !== null : false;
    //     const birthdayBlock = ACTIVATING_DISCOUNT_MANAGEMENT ? birthdayEligible : false;
    //     return !cartHasDiscount && !cartHasSpecial && !birthdayBlock && !appliedPromo && !discountSelected;
    // }, [flags?.cartHasDiscount, flags?.cartHasSpecial, birthdayEligible, appliedPromo, selectedDiscount]);
    const showPromoInput = useMemo(() => {
        const discountSelected = ACTIVATING_DISCOUNT_MANAGEMENT ? selectedDiscount !== null : false;
        return !appliedPromo && !discountSelected;
    }, [appliedPromo, selectedDiscount]);

    /* -------- discount notice -------- */
    const secondPillowSaved = useMemo(() => {
        const s = selectedCartProducts.reduce((acc, p) => acc + (typeof p.secondPillowDiscountAmount === 'number' ? p.secondPillowDiscountAmount : 0), 0);
        return +s.toFixed(2);
    }, [selectedCartProducts]);

    // const discountNotice = useMemo(() => {
    //     const round2 = (n: number) => +Number(n || 0).toFixed(2);
    //     const fmtRub = (n: number) => round2(n).toLocaleString('ru-RU');
    //     const saved = Math.max(0, round2(originalTotal - goodsPayable));
    //     if (hasSecondPillowPromo) {
    //         return {
    //             text: (
    //                 <>
    //                     Применена акция: <b>каждая 2-я подушка -50%</b>
    //                     {secondPillowSaved > 0 ? (
    //                         <>
    //                             {' '}
    //                             (экономия <b>{fmtRub(secondPillowSaved)} ₽</b>)
    //                         </>
    //                     ) : null}
    //                     .
    //                 </>
    //             ),
    //         };
    //     }
    //     if (ACTIVATING_DISCOUNT_MANAGEMENT && selectedDiscount === 'birthday' && birthdayEligible) {
    //         return {
    //             text: (
    //                 <>
    //                     Применена скидка ко дню рождения <b>-25%</b>.
    //                     {isEmployee && sumSpecialEmployee > 0 ? <> Товары с ценой сотрудника не участвуют в -25%.</> : null}
    //                     {saved > 0 ? (
    //                         <>
    //                             {' '}
    //                             Экономия: <b>{fmtRub(saved)} ₽</b>.
    //                         </>
    //                     ) : null}
    //                 </>
    //             ),
    //         };
    //     }
    //     if (
    //         ACTIVATING_DISCOUNT_MANAGEMENT &&
    //         selectedDiscount === 'bonus' &&
    //         typeof bonusEligible === 'number' &&
    //         Number.isFinite(bonusEligible) &&
    //         bonusEligible > 0
    //     ) {
    //         return {
    //             text: (
    //                 <>
    //                     Будут списаны бонусы: <b>{fmtRub(bonusEligible)} ₽</b>.
    //                 </>
    //             ),
    //         };
    //     }
    //     if (appliedPromo?.code) {
    //         const promoHuman = appliedPromo.discountType === 'percentage' ? `-${appliedPromo.discountValue}%` : `-${fmtRub(appliedPromo.discountValue)} ₽`;
    //         return {
    //             text: (
    //                 <>
    //                     Доп. скидка {promoHuman} по промокоду {appliedPromo.code}
    //                     {/* применяется только к товарам без других скидок. */}
    //                 </>
    //             ),
    //         };
    //     }
    //     return null;
    // }, [
    //     hasSecondPillowPromo,
    //     secondPillowSaved,
    //     selectedDiscount,
    //     birthdayEligible,
    //     isEmployee,
    //     sumSpecialEmployee,
    //     originalTotal,
    //     goodsPayable,
    //     bonusEligible,
    //     appliedPromo,
    // ]);

    const discountNotice = useMemo(() => {
        const round2 = (n: number) => +Number(n || 0).toFixed(2);
        const fmtRub = (n: number) => round2(n).toLocaleString('ru-RU');
        const saved = Math.max(0, round2(originalTotal - goodsPayable));
        const parts: React.ReactNode[] = [];
        if (hasSecondPillowPromo) {
            parts.push(
                <span key="second-pillow">
                    Применена акция: <b>каждая 2-я подушка -50%</b>
                    {secondPillowSaved > 0 ? (
                        <>
                            {' '}
                            (экономия <b>{fmtRub(secondPillowSaved)} ₽</b>)
                        </>
                    ) : null}
                    .
                </span>,
            );
        }
        if (ACTIVATING_DISCOUNT_MANAGEMENT && selectedDiscount === 'birthday' && birthdayEligible) {
            parts.push(
                <span key="birthday">
                    Применена скидка ко дню рождения <b>-25%</b>.{isEmployee && sumSpecialEmployee > 0 ? <> Товары с ценой сотрудника не участвуют в -25%.</> : null}
                    {saved > 0 ? (
                        <>
                            {' '}
                            Экономия: <b>{fmtRub(saved)} ₽</b>.
                        </>
                    ) : null}
                </span>,
            );
        }
        if (
            ACTIVATING_DISCOUNT_MANAGEMENT &&
            selectedDiscount === 'bonus' &&
            typeof bonusEligible === 'number' &&
            Number.isFinite(bonusEligible) &&
            bonusEligible > 0
        ) {
            parts.push(
                <span key="bonus">
                    Будут списаны бонусы: <b>{fmtRub(bonusEligible)} ₽</b>.
                </span>,
            );
        }
        if (appliedPromo?.code) {
            const promoHuman = appliedPromo.discountType === 'percentage' ? `-${appliedPromo.discountValue}%` : `-${fmtRub(appliedPromo.discountValue)} ₽`;
            parts.push(
                <span key="promo">
                    Доп. скидка <b>{promoHuman}</b> по промокоду <b>{appliedPromo.code}</b>.
                </span>,
            );
        }
        if (!parts.length) return null;
        return {
            text: (
                <>
                    {parts.map((part, index) => (
                        <React.Fragment key={index}>
                            {index > 0 ? <br /> : null}
                            {part}
                        </React.Fragment>
                    ))}
                </>
            ),
        };
    }, [
        hasSecondPillowPromo,
        secondPillowSaved,
        selectedDiscount,
        birthdayEligible,
        isEmployee,
        sumSpecialEmployee,
        originalTotal,
        goodsPayable,
        bonusEligible,
        appliedPromo,
    ]);

    /* -------- place order -------- */
    const scrollToRecipient = useCallback(() => {
        recipientScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);
    const scrollToDelivery = useCallback(() => {
        setDeliveryNoteHighlight(true);
        deliveryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const handlePlaceOrder = useCallback(async () => {
        setCheckoutAttempted(true);
        if (!hasSelectedProducts) return;
        if (!isAuthenticated) {
            scrollToRecipient();
            recipientSectionRef.current?.openAuthPopup();
            return;
        }
        let checkoutUser = authUser;
        try {
            const preparedUser = await recipientSectionRef.current?.ensureProfileForCheckout();
            if (preparedUser) checkoutUser = preparedUser;
        } catch {
            scrollToRecipient();
            return;
        }
        if (!hasRequiredRecipientData(checkoutUser, String(input.phone ?? ''))) {
            scrollToRecipient();
            return;
        }
        const addressMissing = (input.address ?? '').trim() === '';
        const requiresAddress = effectiveDeliveryMethod !== 'pickup';
        if (requiresAddress && addressMissing) {
            scrollToDelivery();
            return;
        }
        await checkout.submitOrder(checkoutUser);
    }, [hasSelectedProducts, isAuthenticated, authUser, input.phone, input.address, effectiveDeliveryMethod, scrollToRecipient, scrollToDelivery, checkout]);

    const handleRecipientCta = useCallback(() => {
        navigate('/account');
    }, [navigate]);

    return (
        <>
            <Header isMobile={isMobile} />

            <Section>
                <>
                    {/* <div className="cartPageNew__headerWrapper">
                        <BackButton absolute />
                        <SectionHeader title="Корзина" />
                    </div> */}

                    <SectionBody>
                        <>
                            {!hasProducts && (
                                <div>
                                    <Placeholder text={NO_PRODUCTS_IN_CART} />
                                    <ActionLinkButton className="cartPageNew__shopping" to={PATHS.showcase} text="За покупками" ariaLabel="За покупками" />
                                </div>
                            )}

                            {hasProducts && (
                                <div className="cartPageNew">
                                    <div className="cartPageNew__container">
                                        <div className="cartPageNew__layout">
                                            <div className="cartPageNew__left">
                                                <CartItemsSection
                                                    items={cartProducts}
                                                    totalQuantity={fullTotals.quantity}
                                                    selectedIds={selectedIds}
                                                    onToggleSelect={toggleSelect}
                                                    onSelectAll={selectAll}
                                                    onClearSelection={clearSelection}
                                                    onRemoveSelected={removeSelected}
                                                    onRemoveOne={handleRemoveCartItem}
                                                    onWish={handleWishlist}
                                                    appliedPromo={appliedPromo}
                                                />

                                                {/* <PromoUpsellSection /> */}

                                                <div ref={recipientScrollRef}>
                                                    <RecipientSection
                                                        ref={recipientSectionRef}
                                                        authUser={authUser}
                                                        isAuthenticated={isAuthenticated}
                                                        userDataMissing={userDataMissing}
                                                        onCtaClick={handleRecipientCta}
                                                        highlight={checkoutAttempted && (!isAuthenticated || userDataMissing)}
                                                        value={input}
                                                        errors={errors}
                                                        onChange={handleChange}
                                                    />
                                                </div>

                                                <DeliverySection
                                                    ref={deliveryRef}
                                                    authUser={authUser}
                                                    isAuthenticated={isAuthenticated}
                                                    value={input}
                                                    errors={errors}
                                                    onChange={handleChange}
                                                    shippingMethod={shippingMethod}
                                                    setShippingMethod={setShippingMethod}
                                                    shippingCost={effectiveShippingCost}
                                                    shippingError={shippingError}
                                                    shippingStatusText={shippingStatusText}
                                                    isFreeDeliveryEligible={isFreeDeliveryEligible}
                                                    isFreeDeliveryZone={isFreeDeliveryZone}
                                                    freeDeliveryMinTotal={FREE_DELIVERY_MIN_TOTAL}
                                                    goodsPayable={goodsPayable}
                                                    onSelectedAddressChange={setSelectedDeliveryAddress}
                                                    highlightPickupNote={deliveryNoteHighlight}
                                                />

                                                <CommentsSection value={input} onChange={handleChange} />

                                                <PaymentSection
                                                    isAuthenticated={isAuthenticated}
                                                    payWithSBP={payWithSBP}
                                                    setPayWithSBP={setPayWithSBP}
                                                    payWithInstallments={payWithInstallments}
                                                    setPayWithInstallments={setPayWithInstallments}
                                                />
                                            </div>

                                            <aside className="cartPageNew__right" aria-label="К оплате">
                                                <StickySummary
                                                    isAuthenticated={isAuthenticated}
                                                    payableTotal={payableTotal}
                                                    goodsPayable={goodsPayable}
                                                    baseGoodsTotal={summaryBaseGoodsTotal}
                                                    originalTotal={originalTotal}
                                                    effectiveDeliveryMethod={effectiveDeliveryMethod}
                                                    effectiveShippingCost={effectiveShippingCost}
                                                    shippingStatusText={shippingStatusText}
                                                    discountNotice={discountNotice?.text ?? null}
                                                    birthdayEligible={birthdayEligible}
                                                    bonusEligible={bonusEligible}
                                                    showDiscountsUnavailableNotice={showDiscountsUnavailableNotice}
                                                    selectedDiscount={selectedDiscount}
                                                    hasSecondPillowPromo={hasSecondPillowPromo}
                                                    secondPillowSaved={secondPillowSaved}
                                                    onSelectDiscount={handleSelectDiscount}
                                                    showPromoInput={showPromoInput}
                                                    promoCode={promoCodeInput}
                                                    promoError={promoError}
                                                    appliedPromo={appliedPromo}
                                                    onPromoChange={(v) => setPromoCodeInput(v.toUpperCase())}
                                                    onPromoApply={handleApplyPromo}
                                                    onPromoCancel={handleCancelPromo}
                                                    isLoading={isLoading}
                                                    onPlaceOrder={handlePlaceOrder}
                                                    placeOrderDisabled={!hasSelectedProducts}
                                                />
                                            </aside>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {checkout.overQuantity.show && (
                                <OverQuantityModal
                                    products={checkout.overQuantity.products}
                                    onClose={checkout.overQuantity.close}
                                    onConfirm={checkout.overQuantity.confirm}
                                />
                            )}
                        </>
                    </SectionBody>
                </>
            </Section>

            <Footer isMobile={isMobile} />
        </>
    );
};

export default CartPageNew;
